/**
 * The legacy food endpoints answer from the user's selected budget, which is per user rather than
 * per tab. Every call to one names the budget this page is showing as `budget_id`, and the server
 * answers 409 when that is no longer the selected one rather than send another budget's data
 * (docs/local-first-food-data.md, phase 1). Request adds it to every such call.
 *
 * The food/ endpoints take `budget_id` as a parameter of their own and are not touched here.
 *
 * A legacy food write is online-only: it waits for the budget's queued ops to be sent first, so it
 * never runs before an op it might conflict with, and fails with "Needs a connection" while they
 * cannot be (docs/local-first-food-data.md, "Online-only barrier"). Request waits on the barrier.
 */

/** The budget domain's food actions: Budget_Domain::FOOD_ACTIONS. Every meal_idea and grocery_item action is food too. */
const BUDGET_FOOD_ACTIONS = new Set([
	'events',
	'meals',
	'meal_slot_note',
	'search_meals',
	'grocery_lists',
	'grocery_list',
	'active_grocery_list',
	'check_off_grocery_item',
	'add_meal_idea_to_grocery_list',
	'remove_meal_idea_ingredient_from_grocery_list',
	'add_meal_idea_group_to_grocery_list',
]);

let budgetSource = () => null;
let writeBarrier = null;

/** Where the budget comes from: the food store's, once startFood() has set it. */
export function setFoodBudgetSource(source) {
	budgetSource = typeof source === 'function' ? source : () => null;
}

/** Whether `action`, such as 'meal_idea/save_idea' or 'grocery_item/show/12', is a legacy food endpoint. */
export function isLegacyFoodAction(action) {
	const [domain, name] = String(action).split('?')[0].split('/');
	if(domain === 'meal_idea' || domain === 'grocery_item') {
		return true;
	}
	return domain === 'budget' && BUDGET_FOOD_ACTIONS.has(name);
}

/** `data` with `budget_id` added, for a legacy food endpoint while a budget is known. */
export function withFoodBudget(action, data) {
	if(!isLegacyFoodAction(action)) {
		return data;
	}
	const budgetId = budgetSource();
	if(budgetId === null || budgetId === undefined) {
		return data;
	}
	if(data && (typeof data !== 'object' || data.budget_id !== undefined)) {
		return data;
	}
	return { ...(data || {}), budget_id: budgetId };
}

/** What a legacy food write waits for: the store's queued ops drained, once startFood() has set it. */
export function setFoodWriteBarrier(barrier) {
	writeBarrier = typeof barrier === 'function' ? barrier : null;
}

/** For a POST to `action`: a promise to wait for first when it is a legacy food write, otherwise null. */
export function foodWriteBarrier(action) {
	if(!writeBarrier || !isLegacyFoodAction(action)) {
		return null;
	}
	return Promise.resolve().then(() => writeBarrier(action));
}
